import {
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
  Divider,
  InputAdornment,
  Paper,
  ListItemButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useEffect } from "react";
import { searchProductsWithVariants } from "../../api/adminStockApi";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StorefrontIcon from "@mui/icons-material/Storefront";

interface Props {
  items: StockReceiptItem[];
  setItems: React.Dispatch<React.SetStateAction<StockReceiptItem[]>>;
}

type StockReceiptItem = {
  variant_id: number;
  stock_type: "in_store" | "online";
  quantity: number;
  unit_cost: number;
};

type VariantInput = StockReceiptItem & {
  sku: string;
  variant_name: string;
  product_name: string;
  image_url?: string;
};

const ProductVariantPicker = ({ items, setItems }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    { id: number; name: string; image_url?: string; supplier_price: number; variants: any[] }[]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [variantInputs, setVariantInputs] = useState<Record<number, VariantInput>>({});

  const handleSearch = async () => {
    const res = await searchProductsWithVariants(query);
    const normalized = res.map((p: any) => ({
      id: p.product_id,
      name: p.product_name,
      image_url: p.product_image,
      supplier_price: p.supplier_price,
      variants: p.variants,
    }));
    setResults(normalized);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (query.trim()) handleSearch();
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);

    const initialInputs = product.variants.reduce((acc: Record<number, VariantInput>, variant: any) => {
      acc[variant.id] = {
        variant_id: variant.id,
        stock_type: "in_store",
        quantity: 0,
        unit_cost: product.supplier_price || 0,
        sku: variant.sku,
        variant_name: variant.variant_name,
        product_name: product.name,
        image_url: variant.image_url || product.image_url,
      };
      return acc;
    }, {});

    setVariantInputs(initialInputs);
    setOpen(true);
  };

  const handleVariantChange = (variantId: number, field: keyof StockReceiptItem, value: any) => {
    setVariantInputs((prev) => {
      const prevInput = prev[variantId] || {
        variant_id: variantId,
        quantity: 0,
        unit_cost: 0,
        stock_type: "in_store",
        sku: selectedProduct?.variants?.find((v: any) => v.id === variantId)?.sku || "",
        variant_name: selectedProduct?.variants?.find((v: any) => v.id === variantId)?.variant_name || "",
        product_name: selectedProduct?.name || "",
        image_url:
          selectedProduct?.variants?.find((v: any) => v.id === variantId)?.image_url ||
          selectedProduct?.image_url,
      };

      return {
        ...prev,
        [variantId]: {
          ...prevInput,
          [field]: field === "quantity" || field === "unit_cost" ? Number(value) : value,
        },
      };
    });
  };

  const handleAddVariants = () => {
    const validItems = Object.values(variantInputs).filter((v) => v.quantity > 0 && v.unit_cost > 0);
    setItems((prev) => {
      const merged = [...prev];
      for (const item of validItems) {
        const existing = merged.find((i) => i.variant_id === item.variant_id && i.stock_type === item.stock_type);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          merged.push(item);
        }
      }
      return merged;
    });
    setOpen(false);
  };

  const getTotalValue = () => {
    return Object.values(variantInputs).reduce((sum, variant) => {
      return sum + variant.quantity * variant.unit_cost;
    }, 0);
  };

  return (
    <Box>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
          pb: 2,
        }}
      >
        <TextField
          label="Search products or SKUs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} edge="end">
                  <SearchIcon color="primary" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "50px",
            },
          }}
        />
      </Box>

      {results.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <List dense sx={{ py: 0 }}>
            {results.map((product) => (
              <ListItemButton
                key={product.id}
                onClick={() => handleSelectProduct(product)}
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "&:not(:last-child)": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    src={product.image_url}
                    variant="rounded"
                    sx={{ width: 36, height: 36 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={product.name}
                  secondary={
                    <Chip
                      label={`${product.variants.length} variants`}
                      size="small"
                      color="secondary"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
        >
          Add Inventory for {selectedProduct?.name}
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: isMobile ? 1 : 2 }}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
  <TextField
    label="Apply quantity to all"
    type="number"
    size="small"
    onChange={(e) => {
      const value = Number(e.target.value);
      if (!isNaN(value)) {
        setVariantInputs((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((id) => {
            updated[Number(id)] = {
              ...updated[Number(id)],
              quantity: value,
            };
          });
          return updated;
        });
      }
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <InventoryIcon fontSize="small" />
        </InputAdornment>
      ),
    }}
    sx={{ width: 200 }}
  />
</Box>

          <Grid container spacing={2}>
            {selectedProduct?.variants?.map((variant: any) => (
              <Grid item xs={12} key={variant.id} sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2} sx={{ display: "flex", justifyContent: "center" }}>
                    <Avatar
                      src={variant.image_url || selectedProduct.image_url}
                      variant="rounded"
                      sx={{ width: 48, height: 48 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">{variant.variant_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {variant.sku}
                    </Typography>
                    {variant.hamilton_stock !== undefined && (
                      <Chip
                        label={`In Stock: ${variant.hamilton_stock}`}
                        size="small"
                        variant="outlined"
                        color="default"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Qty"
                      type="number"
                      size="small"
                      value={variantInputs[variant.id]?.quantity || 0}
                      onChange={(e) => handleVariantChange(variant.id, "quantity", e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InventoryIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Cost"
                      type="number"
                      size="small"
                      value={variantInputs[variant.id]?.unit_cost || 0}
                      onChange={(e) => handleVariantChange(variant.id, "unit_cost", e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Type"
                      select
                      size="small"
                      value={variantInputs[variant.id]?.stock_type || "in_store"}
                      onChange={(e) => handleVariantChange(variant.id, "stock_type", e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <StorefrontIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value="in_store">In Store</MenuItem>
                      <MenuItem value="online">Online</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "space-between", p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Typography variant="subtitle2" color="primary">
            Total: ${getTotalValue().toFixed(2)}
          </Typography>
          <Box>
            <Button onClick={() => setOpen(false)} variant="outlined" sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddVariants}
              disabled={Object.values(variantInputs).filter((v) => v.quantity > 0 && v.unit_cost > 0).length === 0}
            >
              Add Variants
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductVariantPicker;